// Builds the canonical full URL for a vehicle detail page from a
// `formData`-shaped object. Used by productDetails.tsx to patch
// <link rel="canonical"> and <meta property="og:url"> client-side so the
// native browser Share menu (Chrome Android / Safari iOS) hands WhatsApp
// the full URL — including every query param — instead of the clean SSR
// URL emitted by the Shopify theme. See WI #8432.
//
// Pure helper: no React, no DOM mutation, no side effects.
// Canonical order — must match the URL the storefront uses when constructing
// vehicle detail links from search results. Keep in sync with downstream
// consumers (theme links, share recipients).
const PARAM_ORDER = [
    ["id", "offerId"],
    ["pickUpDate", "pickUpDate"],
    ["dropOffDate", "dropOffDate"],
    ["pickUpTime", "pickUpTime"],
    ["dropOffTime", "dropOffTime"],
    ["pickUpOfficeCode", "pickUpOfficeCode"],
    ["dropOffOfficeCode", "dropOffOfficeCode"],
    ["days", "totalDays"],
    ["currency", "currency"],
    ["userId", "userId"],
    ["idSession", "idSession"],
    ["transactionId", "transactionId"],
    ["category", "category"],
    ["pickupName", "pickupName"],
    ["dropoffName", "dropoffName"],
    ["pickCountry", "pickCountry"],
    ["dropCountry", "dropCountry"],
    ["landingId", "landingId"],
    ["pay", "pay"],
];
function defaultLocation() {
    return {
        origin: window.location.origin,
        pathname: window.location.pathname,
    };
}
/**
 * Composes a canonical, URL-encoded vehicle detail URL.
 *
 * - Iterates `PARAM_ORDER` so query params are emitted in a stable order.
 * - Skips entries whose value is `undefined`, `null`, or `""` so empty
 *   keys never appear in the output.
 * - Uses `URLSearchParams` so reserved / non-ASCII characters are
 *   percent-encoded correctly.
 *
 * @param formData  Source values keyed by internal `formData` field names.
 * @param location  Optional override for `origin` and `pathname` (defaults
 *                  to the current `window.location`). Pass an explicit
 *                  value in unit tests so the result is deterministic.
 */
export function buildVehicleUrl(formData, location = defaultLocation()) {
    const params = new URLSearchParams();
    for (const [urlKey, formKey] of PARAM_ORDER) {
        const rawValue = formData[formKey];
        if (rawValue === undefined || rawValue === null || rawValue === "") {
            continue;
        }
        params.append(urlKey, String(rawValue));
    }
    const query = params.toString();
    const base = `${location.origin}${location.pathname}`;
    return query.length > 0 ? `${base}?${query}` : base;
}
export default buildVehicleUrl;
